'use client';

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, FileText } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';

const LAST_UPDATED = 'August 27, 2026';

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-background">
      {/* Minimal header */}
      <header className="sticky top-0 z-40 bg-card/95 backdrop-blur-md border-b border-border">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <Link
            href="/"
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors min-h-[44px] min-w-[44px] justify-center sm:justify-start sm:min-w-0 sm:px-2"
            aria-label="Back to home"
          >
            <ArrowLeft size={16} />
            <span className="hidden sm:inline">Home</span>
          </Link>
          <div className="flex items-center gap-2 ml-1">
            <AppLogo size={26} />
            <span className="font-semibold text-sm text-foreground">Naija Explorer</span>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        {/* Title block */}
        <div className="mb-10">
          <div className="flex items-center gap-2.5 mb-3">
            <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
              <FileText size={18} className="text-primary" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Terms of Use</h1>
          </div>
          <p className="text-sm text-muted-foreground">Last updated: {LAST_UPDATED}</p>
          <div className="mt-4 p-4 rounded-xl bg-primary/5 border border-primary/15">
            <p className="text-sm text-foreground leading-relaxed">
              By creating an account or using Naija Explorer, you agree to these Terms of Use. 
              Please read them carefully. If you do not agree, please do not use the platform.
            </p>
          </div>
        </div>

        <div className="space-y-8 text-sm text-foreground leading-relaxed">

          <section>
            <h2 className="text-lg font-bold text-foreground mb-3 pb-2 border-b border-border">1. About Naija Explorer</h2>
            <p className="text-muted-foreground">
              Naija Explorer is an educational platform that helps children aged 6–14 learn about Nigerian culture, languages, stories and life skills. 
              It is operated by <strong className="text-foreground">Emmanuel Chiemezie (Possyble)</strong>.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-foreground mb-3 pb-2 border-b border-border">2. Who May Use Naija Explorer</h2>
            <ul className="space-y-2 text-muted-foreground list-disc pl-5">
              <li>Accounts must be created by a parent or legal guardian aged 18 or over.</li>
              <li>Children use the platform through child profiles created and managed by their parent or guardian.</li>
              <li>By creating an account, you confirm that you are the parent or legal guardian responsible for the child profiles on the account.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-foreground mb-3 pb-2 border-b border-border">3. Account Responsibilities</h2>
            <ul className="space-y-2 text-muted-foreground list-disc pl-5">
              <li>You are responsible for keeping your login credentials secure.</li>
              <li>You are responsible for all activity that occurs under your account.</li>
              <li>You must notify us immediately if you suspect unauthorised access to your account.</li>
              <li>You must provide accurate information when creating your account.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-foreground mb-3 pb-2 border-b border-border">4. Acceptable Use</h2>
            <p className="text-muted-foreground mb-3">You agree not to:</p>
            <ul className="space-y-2 text-muted-foreground list-disc pl-5">
              <li>Use the platform for any unlawful purpose</li>
              <li>Attempt to gain unauthorised access to any part of the platform or its infrastructure</li>
              <li>Interfere with or disrupt the platform&apos;s operation</li>
              <li>Reverse-engineer, copy, or redistribute any part of the platform without permission</li>
              <li>Use the platform to transmit harmful, offensive, or inappropriate content</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-foreground mb-3 pb-2 border-b border-border">5. Educational Content</h2>
            <p className="text-muted-foreground">
              All educational content on Naija Explorer — including stories, cultural information, language lessons and life skills — 
              is provided for informational and educational purposes. While we strive for accuracy, we make no warranty that all content is 
              error-free or complete. Content may be updated or removed at any time.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-foreground mb-3 pb-2 border-b border-border">6. Intellectual Property</h2>
            <p className="text-muted-foreground">
              All content, branding, logos, and software on Naija Explorer are the property of{' '}
              <strong className="text-foreground">Emmanuel Chiemezie (Possyble)</strong> or its licensors. 
              You may not reproduce, distribute, or create derivative works without prior written permission.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-foreground mb-3 pb-2 border-b border-border">7. Availability &amp; Service Changes</h2>
            <p className="text-muted-foreground">
              We aim to keep Naija Explorer available at all times but cannot guarantee uninterrupted access. 
              We reserve the right to modify, suspend, or discontinue any part of the service at any time, with or without notice.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-foreground mb-3 pb-2 border-b border-border">8. Limitation of Liability</h2>
            <p className="text-muted-foreground">
              To the fullest extent permitted by law, Naija Explorer and its operators shall not be liable for any indirect, 
              incidental, or consequential damages arising from your use of the platform. 
              The platform is provided &quot;as is&quot; without warranties of any kind.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-foreground mb-3 pb-2 border-b border-border">9. Account Termination</h2>
            <p className="text-muted-foreground">
              You may delete your account at any time by contacting us. We reserve the right to suspend or terminate accounts 
              that violate these Terms of Use.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-foreground mb-3 pb-2 border-b border-border">10. Governing Law</h2>
            <p className="text-muted-foreground">
              These Terms are governed by the laws of <strong className="text-foreground">Nigeria</strong>. 
              Any disputes shall be resolved in the courts of that jurisdiction.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-foreground mb-3 pb-2 border-b border-border">11. Changes to These Terms</h2>
            <p className="text-muted-foreground">
              We may update these Terms from time to time. The &quot;Last updated&quot; date at the top of this page will reflect any changes. 
              Continued use of Naija Explorer after changes constitutes acceptance of the updated Terms.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-foreground mb-3 pb-2 border-b border-border">12. Contact</h2>
            <p className="text-muted-foreground">
              Questions about these Terms? Contact us at{' '}
              <a href="mailto:naijaexplorer@aol.com" className="text-primary hover:underline font-medium">naijaexplorer@aol.com</a>.
            </p>
          </section>
        </div>

        {/* Footer nav */}
        <div className="mt-12 pt-6 border-t border-border flex flex-wrap gap-4 text-sm">
          <Link href="/privacy" className="text-primary hover:underline font-medium">Privacy Policy</Link>
          <Link href="/contact" className="text-primary hover:underline font-medium">Contact / Support</Link>
          <Link href="/" className="text-muted-foreground hover:text-foreground">← Back to Naija Explorer</Link>
        </div>
      </main>
    </div>
  );
}
